import { compactNumber, formatPreciseCurrency } from "@app/number-utils";
import type { DeployLlmGatewayModel } from "@app/types";
import {
  DataTable,
  DataTableCell,
  type DataTableCellRendererProps,
  type DataTableColDef,
  type DataTableProps,
  DataTableTooltip,
  idColDef,
} from "../datatable";

const ModelCostCell = ({ cost }: { cost: number }) =>
  cost === 0 ? (
    <DataTableCell label="Free" />
  ) : (
    <DataTableCell label={formatPreciseCurrency(cost)} />
  );

const DATA_RETENTION_LABELS = { zdr: "ZDR", private: "Private" } as const;

const ZDR_DESCRIPTION =
  "no request or response content sent to this model is kept.";
const PRIVATE_DESCRIPTION =
  "some request and response content sent to this model may be kept for safety review. It's never used for training and can't be read after 30 days.";

/** Plain text of the header tooltip; the grid needs a string to show one. */
export const DATA_RETENTION_TOOLTIP = `ZDR (zero data retention): ${ZDR_DESCRIPTION} Private: ${PRIVATE_DESCRIPTION}`;

const DataRetentionTooltip = () => (
  <DataTableTooltip>
    <p>
      <strong>ZDR</strong> (zero data retention): {ZDR_DESCRIPTION}
    </p>
    <p className="mt-3">
      <strong>Private</strong>: {PRIVATE_DESCRIPTION}
    </p>
  </DataTableTooltip>
);

const modelColDefs: DataTableColDef<DeployLlmGatewayModel>[] = [
  {
    field: "id",
    ...idColDef,
    initialWidth: 220,
    maxWidth: 220,
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployLlmGatewayModel>) => (
      <DataTableCell label={<span className="font-mono">{data.id}</span>} />
    ),
  },
  {
    field: "name",
    headerName: "Name",
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployLlmGatewayModel>) => (
      <DataTableCell label={data.name} />
    ),
  },
  {
    field: "contextLength",
    headerName: "Context Window",
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployLlmGatewayModel>) => (
      <DataTableCell label={compactNumber(data.contextLength)} />
    ),
  },
  {
    field: "inputCostPerMillionTokens",
    headerName: "Input Cost ($/Mtok)",
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployLlmGatewayModel>) => (
      <ModelCostCell cost={data.inputCostPerMillionTokens} />
    ),
  },
  {
    field: "outputCostPerMillionTokens",
    headerName: "Output Cost ($/Mtok)",
    cellRenderer: ({
      data,
    }: DataTableCellRendererProps<DeployLlmGatewayModel>) => (
      <ModelCostCell cost={data.outputCostPerMillionTokens} />
    ),
  },
  {
    colId: "dataRetention",
    headerName: "Data Retention",
    headerTooltip: DATA_RETENTION_TOOLTIP,
    tooltipComponent: DataRetentionTooltip,
    // Sort on the label shown, so an unreported mode sorts with the blanks.
    valueGetter: ({ data }) =>
      data?.dataRetentionMode && DATA_RETENTION_LABELS[data.dataRetentionMode],
    // Sortable, but left out of free-text search.
    getQuickFilterText: () => "",
    cellRenderer: ({
      value,
    }: DataTableCellRendererProps<DeployLlmGatewayModel>) => (
      <DataTableCell label={value ?? "—"} />
    ),
  },
];

export const LlmGatewayModelTable = (
  props: Omit<
    DataTableProps<DeployLlmGatewayModel>,
    "colDefs" | "pageSize" | "rowId"
  >,
) => (
  <DataTable<DeployLlmGatewayModel>
    colDefs={modelColDefs}
    pageSize={10}
    rowId={(model) => model.id}
    {...props}
  />
);
